import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from '@/content/i18n/en.json';
import ko from '@/content/i18n/ko.json';

/**
 * Web i18n bootstrap.
 *
 * - Default + fallback locale: `ko`. We keep Korean as the canonical copy because
 *   it ships first and the source-of-truth strings live in `content/i18n/ko.json`.
 * - Supported: `ko`, `en`. Adding a locale = drop a JSON file alongside and
 *   register it in `resources`.
 * - Detection order favours an explicit `?lng=` querystring (handy for QA and
 *   share links), then sticky `localStorage`, then the browser's `navigator`
 *   preference, then the `<html lang>` attribute.
 * - Single `common` namespace — translations live under a flat path like
 *   `home.hero_title` so the same key works whether the consumer scopes to
 *   `useTranslation('common')` or default.
 *
 * Web/CLAUDE.md previously said "no i18n library yet" — that exception has been
 * lifted as the bilingual surface ships with the launch.
 */

export const SUPPORTED_LOCALES = ['ko', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'ko';

const LANG_STORAGE_KEY = 'lucy.lang';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ko: { common: ko },
      en: { common: en },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: 'common',
    ns: ['common'],
    // `escapeValue: false` is correct here and MUST stay false: React escapes
    // every string it renders as a text child, so double-escaping by i18next
    // would print literal `&amp;`/`&lt;` in the UI. The safety of this setting
    // depends entirely on `t()` output only ever reaching the DOM as a React
    // text child.
    //
    // XSS FOOTGUN — do NOT feed `t()` output into `dangerouslySetInnerHTML`, nor
    // into an i18next `<Trans>` whose translation embeds raw HTML. Interpolation
    // values include untrusted data (e.g. invite code, server group name from
    // the API), and with escaping off those would be injected verbatim. If a
    // string ever genuinely needs HTML, sanitize it (see `lib/href.ts` /
    // `shadow-html.tsx`) before rendering — never rely on i18next to escape it.
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: LANG_STORAGE_KEY,
    },
  });

// Keep `<html lang>` in sync so screen readers + browser UI agree with the
// active translation.
const syncHtmlLang = (lng: string): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', lng);
};

syncHtmlLang(i18n.resolvedLanguage ?? DEFAULT_LOCALE);
i18n.on('languageChanged', syncHtmlLang);

export { i18n };
